import type { ComponentPropsWithoutRef } from "react";

import { cn } from "@/lib/utils";

// MUI's `Card variant='outlined'` under the donor theme — `@modsy/react-ui`'s
// `Card`, which the donor's item card and item row both are
// (`items-card-list/item-card.tsx:127`, `items-data-list/item-data-row.tsx`),
// and which the catalog spelled as one identical class string on the vendored
// shadcn `Card` in two files, undoing that primitive's padding, gap, 12px
// corners and shadow each time.
//
// `Card.mjs` is `Paper` plus `overflow: hidden`, and outlined `Paper` is:
//
//   - a 1px border — at the donor theme's `borderColor: 'rgba(0, 0, 0, 0.2)'`
//     (`theme.ts:363-369`, `MuiCard`), not MUI's own 0.12 divider,
//   - 4px corners (`shape.borderRadius`, not overridden), `#fff` under
//     `text.primary`, and no shadow,
//   - **no padding and no layout**: MUI's card is a plain block, and its
//     content (the donor's `VStack padding={2}`) brings both.
//
// Positioning is the caller's: the item card and row are `relative` because
// their corner controls and status overlay are absolutely placed over them.
//
// **`variant="elevation"`** is MUI's *default* `Card` — `Paper` at
// `elevation={1}` — which the community link cards draw (#754, `LinkCard.tsx`,
// `AddLinkCard.tsx`). The donor theme replaces MUI's drop shadows with rings,
// so `shadows[1]` is `0 0 0 1px #e0e0e0`, and the theme's `borderColor`
// override paints nothing on a card with no border width: a ring, no border,
// measured on live modsy.

const VARIANT_CLASSES = {
  outlined: "border-lennar-card-outline border",
  elevation: "shadow-[0_0_0_1px_var(--color-lennar-grey-300)]",
} as const;

export interface MuiCardProps extends ComponentPropsWithoutRef<"div"> {
  /** MUI's `variant` — `outlined` here by default, the catalog's every card. */
  variant?: keyof typeof VARIANT_CLASSES;
}

export function MuiCard({ variant = "outlined", className, ...props }: MuiCardProps) {
  return (
    <div
      data-slot="mui-card"
      {...props}
      className={cn(
        "text-lennar-ink overflow-hidden rounded-[4px] bg-white",
        VARIANT_CLASSES[variant],
        className,
      )}
    />
  );
}
